import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaPhoneSlash, FaDesktop, FaStopCircle } from "react-icons/fa";
import { io } from "socket.io-client";

const STUN_SERVERS = [{ urls: ["stun:stun.l.google.com:19302"] }];
const SIGNALING_URL = process.env.REACT_APP_SIGNALING_URL || "";
const SIGNALING_NAMESPACE = process.env.REACT_APP_SIGNALING_NAMESPACE || "";

const createParticipantId = (role = "participant") =>
  `${role}-${Math.random().toString(36).slice(2, 10)}`;

const mediaErrorMessage = (mediaError = {}) => {
  switch (mediaError.name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
      return "Allow camera and microphone access in your browser settings, then reload the classroom.";
    case "NotReadableError":
    case "TrackStartError":
      return "Your camera or microphone is already being used by another browser or application. Close it there and reload the classroom.";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "No usable camera or microphone was found on this device.";
    case "OverconstrainedError":
    case "ConstraintNotSatisfiedError":
      return "The selected camera or microphone settings are not supported by this device.";
    case "SecurityError":
      return "Camera and microphone access requires HTTPS (localhost is allowed for development).";
    case "AbortError":
      return "Camera or microphone startup was interrupted. Close other media apps and reload the classroom.";
    default:
      return `Unable to access camera or microphone${mediaError.message ? `: ${mediaError.message}` : "."}`;
  }
};

const VideoTile = ({ title, muted, stream, className = "", videoClassName = "h-44 sm:h-56" }) => {
  const videoRef = useRef(null);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);

  useEffect(() => {
    if (!videoRef.current) return;
    videoRef.current.srcObject = stream || null;
    setPlaybackBlocked(false);
    if (stream) {
      const playback = videoRef.current.play();
      if (playback?.catch) {
        playback.catch(() => {
          if (!muted) setPlaybackBlocked(true);
        });
      }
    }
  }, [muted, stream]);

  const enablePlayback = async () => {
    try {
      await videoRef.current?.play();
      setPlaybackBlocked(false);
    } catch {
      setPlaybackBlocked(true);
    }
  };

  return (
    <div className={`relative overflow-hidden rounded-2xl border border-slate-700 bg-slate-900 ${className}`}>
      <div className="flex items-center justify-between border-b border-slate-700 px-3 py-2 text-xs text-slate-300">
        <span className="font-semibold tracking-wide">{title}</span>
        {muted && <span className="rounded-full bg-slate-700 px-2 py-0.5 text-[10px] uppercase">Muted</span>}
      </div>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={muted}
        className={`w-full bg-black object-cover ${videoClassName}`}
      />
      {playbackBlocked && (
        <button
          type="button"
          onClick={enablePlayback}
          className="absolute inset-x-3 bottom-3 rounded-xl bg-sky-600 px-3 py-2 text-xs font-bold text-white shadow-lg"
        >
          Tap to enable classroom audio
        </button>
      )}
    </div>
  );
};

export default function WebRTCClassRoom({
  roomId,
  role = "student",
  displayName = "Participant",
  guestToken,
  onClose,
  onParticipantJoin,
  allowStudentAudio = true,
  allowStudentVideo = false,
}) {
  const isHost = role === "teacher" || role === "host";
  const canUseMicrophone = isHost || allowStudentAudio;
  const canUseCamera = isHost || allowStudentVideo;
  const participantId = useMemo(() => createParticipantId(role), [role]);
  const [localStream, setLocalStream] = useState(null);
  const [screenStream, setScreenStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState([]);
  const [micOn, setMicOn] = useState(canUseMicrophone);
  const [cameraOn, setCameraOn] = useState(canUseCamera);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [connectionState, setConnectionState] = useState("connecting");

  const socketRef = useRef(null);
  const localStreamRef = useRef(null);
  const screenTrackRef = useRef(null);
  const screenStreamRef = useRef(null);
  const peersRef = useRef(new Map());
  const remoteMetaRef = useRef(new Map());

  const upsertRemoteStream = (peerId, stream) => {
    setRemoteStreams((previous) => {
      const next = previous.filter((item) => item.peerId !== peerId);
      const meta = remoteMetaRef.current.get(peerId) || { name: "Participant" };
      next.push({ peerId, stream, name: meta.name });
      return next;
    });
  };

  const removePeer = (peerId) => {
    const peer = peersRef.current.get(peerId);
    if (peer) {
      try {
        peer.close();
      } catch {
        // Ignore close errors.
      }
      peersRef.current.delete(peerId);
    }
    remoteMetaRef.current.delete(peerId);
    setRemoteStreams((previous) => previous.filter((item) => item.peerId !== peerId));
  };

  const sendSignal = (message) => {
    if (!socketRef.current) return;
    socketRef.current.emit("class:signal", {
      roomId,
      from: participantId,
      ts: Date.now(),
      meta: { name: displayName, role },
      ...message,
    });
  };

  const ensurePeer = (peerId) => {
    if (peersRef.current.has(peerId)) {
      return peersRef.current.get(peerId);
    }

    const peer = new RTCPeerConnection({ iceServers: STUN_SERVERS });

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => {
        const outgoingTrack = track.kind === "video" && screenTrackRef.current
          ? screenTrackRef.current
          : track;
        peer.addTrack(outgoingTrack, localStreamRef.current);
      });
    }

    peer.onicecandidate = (event) => {
      if (!event.candidate) return;
      sendSignal({ type: "ice", to: peerId, payload: event.candidate });
    };

    peer.ontrack = (event) => {
      const [stream] = event.streams;
      if (stream) {
        upsertRemoteStream(peerId, stream);
      }
    };

    peer.onconnectionstatechange = () => {
      const state = peer.connectionState;
      if (state === "failed" || state === "closed" || state === "disconnected") {
        removePeer(peerId);
      }
    };

    peersRef.current.set(peerId, peer);
    return peer;
  };

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      if (!roomId) return;
      setConnectionState("connecting");

      if (!SIGNALING_URL) {
        setError("Signaling server is not configured. Set REACT_APP_SIGNALING_URL.");
        setConnectionState("error");
        return;
      }

      let requestingMedia = true;
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          const unsupportedError = new Error("Media devices are unavailable in this browser or page context.");
          unsupportedError.name = window.isSecureContext ? "NotSupportedError" : "SecurityError";
          throw unsupportedError;
        }

        // Release tracks left by an earlier classroom mount before asking the
        // browser to acquire the physical devices again.
        if (localStreamRef.current) {
          localStreamRef.current.getTracks().forEach((track) => track.stop());
          localStreamRef.current = null;
          setLocalStream(null);
        }
        const stream = canUseMicrophone || canUseCamera
          ? await navigator.mediaDevices.getUserMedia({
              video: canUseCamera,
              audio: canUseMicrophone,
            })
          : new MediaStream();

        if (!mounted) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        setLocalStream(stream);
        localStreamRef.current = stream;
        setMicOn(stream.getAudioTracks().length > 0);
        setCameraOn(stream.getVideoTracks().length > 0);
        requestingMedia = false;

        const signalingEndpoint = `${SIGNALING_URL.replace(/\/$/, "")}${SIGNALING_NAMESPACE}`;
        // Authenticated hosts use their HttpOnly session. Public students have
        // no login session, so send the admitted attendance token in Socket.IO
        // handshake auth (never in the URL/query string).
        const socket = io(signalingEndpoint, {
          transports: ["websocket"],
          withCredentials: true,
          auth: guestToken
            ? { guestToken, meetingId: roomId }
            : undefined,
          query: {
            roomId,
            participantId,
            role,
            displayName,
          },
        });

        socketRef.current = socket;

        socket.on("connect", () => {
          if (!mounted) return;
          setConnectionState("connected");
          setReady(true);
socket.emit("class:join", {
  roomId: roomId,
  participantId: participantId,
  displayName: displayName,
});        });

        socket.on("disconnect", () => {
          if (!mounted) return;
          setConnectionState("disconnected");
        });

        socket.on("connect_error", () => {
          if (!mounted) return;
          setConnectionState("error");
          setError("Unable to connect to signaling server.");
        });

        socket.on("class:participants", async ({ participants = [] } = {}) => {
          if (role !== "teacher") return;
          for (const participant of participants) {
            const peerId = participant?.participantId;
            if (!peerId || peerId === participantId) continue;
            if (participant?.meta?.name) {
              remoteMetaRef.current.set(peerId, { name: participant.meta.name });
            }
            try {
              const peer = ensurePeer(peerId);
              const offer = await peer.createOffer();
              await peer.setLocalDescription(offer);
              sendSignal({ type: "offer", to: peerId, payload: offer });
            } catch {
              // Ignore per-peer init failures.
            }
          }
        });

        socket.on("class:participant-joined", async ({ participantId: peerId, meta } = {}) => {
          if (!peerId || peerId === participantId) return;
          if (meta?.name) {
            remoteMetaRef.current.set(peerId, { name: meta.name });
          }

          if (role === "teacher") {
            try {
              const peer = ensurePeer(peerId);
              const offer = await peer.createOffer();
              await peer.setLocalDescription(offer);
              sendSignal({ type: "offer", to: peerId, payload: offer });
              if (typeof onParticipantJoin === "function") {
                onParticipantJoin(meta || {});
              }
            } catch {
              // Ignore offer creation failures.
            }
          }
        });

        socket.on("class:participant-left", ({ participantId: peerId } = {}) => {
          if (!peerId) return;
          removePeer(peerId);
        });

        const handleClassEnded = ({ roomId: endedRoomId } = {}) => {
          if (endedRoomId && String(endedRoomId) !== String(roomId)) return;
          setRemoteStreams([]);
          setConnectionState("ended");
          setError("This live class has ended.");
          if (typeof onClose === "function") onClose();
        };

        // Support both event names while signaling servers are upgraded.
        socket.on("class:ended", handleClassEnded);
        socket.on("class:end", handleClassEnded);

        socket.on("class:signal", async (message = {}) => {
          if (message.roomId !== roomId) return;
          if (message.from === participantId) return;
          if (message.to && message.to !== participantId) return;

          if (message.meta?.name) {
            remoteMetaRef.current.set(message.from, { name: message.meta.name });
          }

          try {
            if (message.type === "offer" && role !== "teacher") {
              const peer = ensurePeer(message.from);
              await peer.setRemoteDescription(new RTCSessionDescription(message.payload));
              const answer = await peer.createAnswer();
              await peer.setLocalDescription(answer);
              sendSignal({ type: "answer", to: message.from, payload: answer });
              return;
            }

            if (message.type === "answer" && role === "teacher") {
              const peer = ensurePeer(message.from);
              await peer.setRemoteDescription(new RTCSessionDescription(message.payload));
              return;
            }

            if (message.type === "ice") {
              const peer = ensurePeer(message.from);
              await peer.addIceCandidate(new RTCIceCandidate(message.payload));
              return;
            }

            if (message.type === "leave") {
              removePeer(message.from);
            }
          } catch {
            // Ignore per-message WebRTC negotiation errors.
          }
        });
      } catch (initializationError) {
        console.error(
          requestingMedia ? "getUserMedia failed:" : "Classroom initialization failed:",
          initializationError?.name,
          initializationError?.message,
        );
        setConnectionState("error");
        setError(requestingMedia
          ? mediaErrorMessage(initializationError)
          : "Unable to initialize the classroom connection. Please reload and try again.");
      }
    };

    initialize();

    return () => {
      mounted = false;
      sendSignal({ type: "leave" });

      if (socketRef.current) {
        socketRef.current.emit("class:leave", { roomId, participantId });
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      peersRef.current.forEach((peer) => {
        try {
          peer.close();
        } catch {
          // Ignore close errors.
        }
      });
      peersRef.current.clear();

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
      }
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach((track) => track.stop());
        screenStreamRef.current = null;
      }
    };
  }, [canUseCamera, canUseMicrophone, displayName, guestToken, onParticipantJoin, participantId, role, roomId]);

  const toggleMic = () => {
    if (!canUseMicrophone) return;
    if (!localStreamRef.current) return;
    const nextState = !micOn;
    localStreamRef.current.getAudioTracks().forEach((track) => {
      track.enabled = nextState;
    });
    setMicOn(nextState);
  };

  const toggleCamera = () => {
    if (!canUseCamera) return;
    if (!localStreamRef.current) return;
    const nextState = !cameraOn;
    localStreamRef.current.getVideoTracks().forEach((track) => {
      track.enabled = nextState;
    });
    setCameraOn(nextState);
  };

  const stopScreenShare = async () => {
    const screenTrack = screenTrackRef.current;
    screenTrackRef.current = null;

    // Restore camera video track in all peer connections
    const cameraTrack = localStreamRef.current?.getVideoTracks()[0];
    if (cameraTrack) {
      await Promise.allSettled(Array.from(peersRef.current.values()).map((peer) => {
        const sender = peer.getSenders().find((s) => s.track?.kind === "video");
        return sender ? sender.replaceTrack(cameraTrack) : Promise.resolve();
      }));
    }

    if (screenTrack) screenTrack.onended = null;
    const capturedStream = screenStreamRef.current;
    screenStreamRef.current = null;
    if (capturedStream) capturedStream.getTracks().forEach((track) => track.stop());
    else if (screenTrack?.readyState !== "ended") screenTrack.stop();

    setScreenStream(null);
    setIsScreenSharing(false);
  };

  const toggleScreenShare = async () => {
    if (!isHost) return;
    if (isScreenSharing) {
      stopScreenShare();
      return;
    }
    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      const screenTrack = screenStream.getVideoTracks()[0];
      screenStreamRef.current = screenStream;
      screenTrackRef.current = screenTrack;
      setScreenStream(screenStream);

      // Replace video track in all active peer connections
      peersRef.current.forEach((peer) => {
        const sender = peer.getSenders().find((s) => s.track?.kind === "video");
        if (sender) sender.replaceTrack(screenTrack).catch(() => {});
      });

      // Auto-stop when user clicks browser's "Stop sharing" button
      screenTrack.onended = () => { void stopScreenShare(); };

      setIsScreenSharing(true);
    } catch {
      // User cancelled or permission denied — silently ignore
    }
  };

  if (!roomId) {
    return null;
  }

  return (
    <div className="mt-4 overflow-hidden rounded-3xl border border-slate-800 bg-slate-950 text-slate-100 shadow-2xl">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 px-4 py-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-sky-300">Live Classroom</p>
          <h3 className="text-base font-semibold sm:text-lg">Class code {roomId}</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${connectionState === "connected" ? "bg-emerald-900/60 text-emerald-200" : connectionState === "connecting" ? "bg-amber-900/60 text-amber-200" : "bg-rose-900/60 text-rose-200"}`}>
            {connectionState === "connected" ? "Connected" : connectionState === "connecting" ? "Connecting" : "Connection issue"}
          </span>
          <span className="rounded-full bg-slate-800 px-3 py-1 text-xs font-semibold text-slate-200">
            {1 + remoteStreams.length} participant{remoteStreams.length ? "s" : ""}
          </span>
        </div>
      </div>

      {error && <div className="mx-4 mt-3 rounded-xl border border-rose-700 bg-rose-950/40 px-3 py-2 text-sm text-rose-200">{error}</div>}
      {!error && !ready && <div className="mx-4 mt-3 text-sm text-slate-400">Preparing camera and peer connection...</div>}

      <div className="grid gap-3 p-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <VideoTile
          title={isScreenSharing ? `${displayName} (Presenting)` : `${displayName} (You)`}
          muted
          stream={screenStream || localStream}
          className="min-h-[320px] lg:min-h-[480px]"
          videoClassName="h-[320px] sm:h-[420px] lg:h-[480px]"
        />

        <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Participants</p>
          <div className="space-y-2">
            {remoteStreams.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-700 px-3 py-4 text-center text-xs text-slate-400">
                Waiting for participants to join
              </div>
            )}
            {remoteStreams.map((item) => (
              <VideoTile
                key={item.peerId}
                title={item.name || "Participant"}
                muted={false}
                stream={item.stream}
                videoClassName="h-36"
              />
            ))}
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 border-t border-slate-800 bg-slate-950/95 px-4 py-3 backdrop-blur">
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={toggleMic}
            aria-label={!canUseMicrophone ? "Microphone disabled by instructor" : micOn ? "Mute microphone" : "Unmute microphone"}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold ${!canUseMicrophone ? "cursor-help bg-slate-800 text-slate-500" : micOn ? "bg-emerald-600 text-white" : "bg-slate-700 text-slate-200"}`}
          >
            {micOn && canUseMicrophone ? <FaMicrophone /> : <FaMicrophoneSlash />}
            {!canUseMicrophone ? "Mic restricted" : micOn ? "Mic on" : "Mic off"}
          </button>
          <button
            type="button"
            onClick={toggleCamera}
            aria-label={!canUseCamera ? "Camera disabled by instructor" : cameraOn ? "Turn camera off" : "Turn camera on"}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold ${!canUseCamera ? "cursor-help bg-slate-800 text-slate-500" : cameraOn ? "bg-sky-600 text-white" : "bg-slate-700 text-slate-200"}`}
          >
            {cameraOn && canUseCamera ? <FaVideo /> : <FaVideoSlash />}
            {!canUseCamera ? "Camera restricted" : cameraOn ? "Camera on" : "Camera off"}
          </button>
          {isHost && <button
            type="button"
            onClick={toggleScreenShare}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold ${isScreenSharing ? "bg-amber-500 text-white" : "bg-slate-700 text-slate-200"}`}
          >
            {isScreenSharing ? <FaStopCircle /> : <FaDesktop />}
            {isScreenSharing ? "Stop sharing" : "Share screen"}
          </button>}
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-4 py-2 text-xs font-semibold text-white"
          >
            <FaPhoneSlash />
            Leave class
          </button>
        </div>
      </div>
    </div>
  );
}
